import { PositionsTable } from '@Components/Tables/PositionsTable';
import { BILLING_API, STAMMDATEN_API } from '@Constants/backendConstants';
import { getFrameLabel } from '@Constants/getFrameLabel';
import { Permission } from '@Constants/Permission.g';
import { addressColumns, serviceColumns } from '@Constants/TableColumns';
import { useHasPermission } from '@Contexts/appContext';
import { useAddServices } from '@Hooks/Billing';
import { FrameEditAddress } from '@Modules/Stammdaten';
import {
    Button,
    Card,
    Column,
    EditFrameMode,
    EditFrameShell,
    FrameEditBase,
    Grid,
    GridColumn,
    GridColumns,
    iconSizeSm,
    type IFrameBodyProps,
    type IFrameEditShellProps,
    TextInput,
    Select,
    useAutoConfirmed,
    useEditSubObject,
    useFrameEditBaseNavigation,
    useGuiBinder,
    useLinkedField,
    useLinkProperty,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { IconLink, IconPlus } from '@tabler/icons-react';
import {
    type ApiAddress,
    type ApiPriceList,
    type ApiPriceListPosition,
    type ApiService,
    EnumFrontendLayoutSetting,
    EnumPriceListType,
    priceListOptions,
} from '@Model';
import { useCallback, useMemo, useState } from 'react';
import { FramePriceListPosition } from './FramePriceListPosition';
import { EnumBusinessState, enumBusinessStateOptions } from '@Model';
import { useParams } from 'react-router';
import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import { useBadges } from '@Hooks/useBadges';
import { TableModalWithLayoutSettings } from '@Components/Tables/TableModalWithLayoutSettings';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { useListApi } from '@Hooks/useListApi';
import { ServicesRepository } from '@Repositories/Stammdaten';

const priceListPositionColumns: Column<ApiPriceListPosition>[] = [
    { name: 'service.display_string', label: 'Leistung' },
    {
        name: 'price_per_unit',
        label: 'Preis',
        type: 'currency',
        decimalPlaces: (rowData) => rowData.decimal_places_price,
    },
    { name: 'valid_from', label: 'Gültig ab', type: 'date', filterVariant: 'day-range' },
    { name: 'valid_until', label: 'Gültig bis', type: 'date', filterVariant: 'day-range' },
];

export function FrameEditPriceList(props: IFrameEditShellProps<ApiPriceList>) {
    const hasPermission = useHasPermission();
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const frameEditBaseNavigationEvents = useFrameEditBaseNavigation();

    return (
        <FrameEditBase<ApiPriceList>
            apiResourcePath={`${BILLING_API}/price_lists`}
            renderer={FrameEditPriceListBody}
            key={primaryKey}
            primaryKey={primaryKey}
            embedded={props.embedded || props.primaryKey !== undefined}
            canEdit={props.canEdit !== false && hasPermission(Permission.BillingPriceListsUpdate)}
            canCreate={props.canCreate !== false && hasPermission(Permission.BillingPriceListsCreate)}
            canDelete={props.canDelete !== false && hasPermission(Permission.BillingPriceListsDelete)}
            frameProps={{
                events: {
                    ...frameEditBaseNavigationEvents,
                    ...props.events,
                },
            }}
        />
    );
}

function FrameEditPriceListBody(props: IFrameBodyProps<ApiPriceList>) {
    const i18n = useTranslate('');

    const apiPriceList = props.data;
    const baseApiObjectPath = `${props.apiResourcePath}/${props.data.id}`;

    const binder = useGuiBinder<ApiPriceList>({
        ...props,
        canEdit: props.mode !== EditFrameMode.View,
    });

    const { linkApi: addressLinkApi, events: addressOverlayEvents } = useLinkedField<ApiPriceList, ApiAddress>(
        baseApiObjectPath,
        'address',
        FrameEditAddress,
        true,
        props.requestUpdateState,
    );

    const typeField = useAutoConfirmed<ApiPriceList, EnumPriceListType>(binder, 'type');
    const labelField = useProp(binder, 'label');
    const shorthandField = useProp(binder, 'shorthand');
    const businessStateField = useAutoConfirmed<ApiPriceList, EnumBusinessState>(binder, 'business_state');
    const addressField = useLinkProperty(binder, addressLinkApi);
    const remarksField = useProp(binder, 'remarks');

    const [isServicesModalOpen, setIsServiceModalOpen] = useState(false);
    const {
        startAddAllMissingServices,
        startAddServices,
        isPending: isAddAllServicesPending,
    } = useAddServices(apiPriceList.id);

    const isBaseList = apiPriceList.type === EnumPriceListType.Base;
    const isCustomer = apiPriceList.type === EnumPriceListType.Customer;
    const isSiteList = apiPriceList.type === EnumPriceListType.Site;
    const showAddress = isCustomer || isSiteList;

    const { deleteSubObjects: deletePositions, requestUpdateSubObject: requestUpdatePosition } = useEditSubObject<
        ApiPriceListPosition,
        ApiPriceList
    >(baseApiObjectPath, 'positions', props.requestUpdateState);

    const requestUpdateState = props.requestUpdateState;
    const importAllServices = useCallback(
        () => requestUpdateState(startAddAllMissingServices),
        [requestUpdateState, startAddAllMissingServices],
    );

    const importServices = useCallback(
        (serviceIds: string[]) => {
            setIsServiceModalOpen(false);
            return requestUpdateState(() => startAddServices(serviceIds));
        },
        [requestUpdateState, startAddServices],
    );

    const positionToolbarElements = useMemo(() => {
        const toolbarElements = [
            'search',
            <Button
                key="btnAddService"
                onClick={() => setIsServiceModalOpen(true)}
                size="sm"
                appearance="outlined"
                locked={props.mode === EditFrameMode.View || isAddAllServicesPending}
            >
                <IconPlus size={iconSizeSm} />
                &nbsp;{i18n('Leistungen hinzufügen')}
            </Button>,
            'delete',
        ];

        if (isBaseList) {
            toolbarElements.push(
                <Button
                    key="btnAddAllServices"
                    onClick={importAllServices}
                    appearance="outlined"
                    size="sm"
                    locked={props.mode === EditFrameMode.View || isAddAllServicesPending}
                >
                    <IconLink size={iconSizeSm} />
                    &nbsp;{i18n('Alle Leistungen importieren')}
                </Button>,
            );
        }
        return toolbarElements;
    }, [isBaseList, props.mode, isAddAllServicesPending, importAllServices, i18n]);

    const servicesApi = useListApi(ServicesRepository, STAMMDATEN_API);

    const priceListBadges = useBadges(apiPriceList);

    const positionsLabel = `${i18n('Leistungen & Preise')} (${apiPriceList.positions.length})`;

    return (
        <EditFrameShell
            headerProps={{
                label: getFrameLabel('Preisliste', props.mode),
                resourceLabel: 'Preisliste',
                ...props.events,
            }}
            bannerProps={{
                mode: props.mode,
                loading: props.stateQueueDepth > 0,
                ...props.events,
            }}
        >
            <Card name="headdata" border={'withoutBorder'} label="Allgemeine Informationen" badges={priceListBadges}>
                <Grid>
                    <GridColumns spans={[3, 3, 3, 3, 3]}>
                        <Select<EnumPriceListType> items={priceListOptions} {...typeField} label={i18n('Typ')} />
                        <TextInput {...labelField} label={i18n('Bezeichnung')} />
                        <TextInput {...shorthandField} label={i18n('Suchcode')} />
                        <Select items={enumBusinessStateOptions} {...businessStateField} label={i18n('Status')} />
                    </GridColumns>
                    {showAddress && (
                        <GridColumn span={12}>
                            <LinkedFieldWithLayoutSettings<ApiAddress>
                                layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                                {...addressField}
                                {...addressOverlayEvents}
                                columns={addressColumns}
                                label={
                                    apiPriceList.type == EnumPriceListType.Customer ? i18n('Kunde') : i18n('Anschrift')
                                }
                                dropdownPreview={AddressLinkDropdownPreview}
                            />
                        </GridColumn>
                    )}
                    <GridColumn span={12}>
                        <TextInput {...remarksField} label={i18n('Bemerkung')} inputSize={3} />
                    </GridColumn>
                </Grid>
            </Card>

            <Card name="positions" label={positionsLabel}>
                <PositionsTable<ApiPriceListPosition>
                    layoutSettingsContext={EnumFrontendLayoutSetting.PriceListPositionsTableLayout}
                    items={apiPriceList.positions}
                    columns={priceListPositionColumns}
                    name={'LinkedOrderPositions'}
                    editable={props.mode !== EditFrameMode.View}
                    onAdd={() => undefined}
                    onDelete={deletePositions}
                    toolbarElements={positionToolbarElements}
                    renderSubComponent={(row) => (
                        <FramePriceListPosition
                            key={row.id}
                            data={row}
                            meta={undefined}
                            canEdit={props.mode !== EditFrameMode.View}
                            apiResourcePath={`${baseApiObjectPath}/positions`}
                            refetchData={props.refetchData}
                            requestUpdateState={(updater) => requestUpdatePosition(updater, row.id)}
                            stateQueueDepth={props.stateQueueDepth}
                        />
                    )}
                />
            </Card>
            <TableModalWithLayoutSettings<ApiService>
                layoutSettingsContext={EnumFrontendLayoutSetting.ServicesTableLayout}
                isOpen={isServicesModalOpen}
                onClose={() => setIsServiceModalOpen(false)}
                tableProps={{
                    name: 'selectServices',
                    columns: serviceColumns,
                    items: [],
                    loader: servicesApi.loadPage,
                }}
                title={i18n('Leistungen auswählen')}
                multiple
                actions={[
                    {
                        name: 'addServices',
                        label: 'Leistungen hinzufügen',
                        action: importServices,
                    },
                ]}
            />
        </EditFrameShell>
    );
}
